import { useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { Card } from "reactstrap";
import { toast } from "react-toastify";
import PageHeader from "../../components/PageHeader";
import HowItWorksButton from "../../components/howItWorks/HowItWorksButton";
import ConfirmDeleteModal from "../../ui/components/ConfirmDeleteModal";
import HighlightCard from "../../ui/components/HighlightCard";
import { InlineLoader } from "../../components/loader/CharacterizationLoader";
import { useIsAdminViewMode } from "../../hooks/useViewMode";
import {
  deleteConfrontation,
  getConfrontationById,
} from "../../services/confrontation";
import {
  mapApiDocumentToListRow,
  buildDetailSectionTitle,
  formatConfrontationParentCategoryLabel,
} from "./confrontationMappers";
import {
  computeConfrontationKpis,
  formatLocaleNumber,
  formatPercentValue,
  getLocaleForLanguage,
  mapParentCategoryRowsToComparisonRows,
} from "./confrontationMetrics";
import ConfrontationComparisonChart from "./ConfrontationComparisonChart";
import { showErrorToast } from "../../utils/errorHandler";
import wmaeIcon from "../../static/assets/dashboard-sorting-variation.svg";
import maxGapIcon from "../../static/assets/dashboard-sorting-error.svg";
import contributorIcon from "../../static/assets/dashboard-waste-analyzed.svg";

const ConfrontationDetail = () => {
  const token = Cookies.get("userToken");
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const isAdmin = useIsAdminViewMode();
  const confrontationFromState = location.state?.confrontation;
  const locale = getLocaleForLanguage(i18n.language);

  const [apiDoc, setApiDoc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token || !id) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setLoadError(false);
      try {
        const response = await getConfrontationById(id);
        const doc =
          response.data && response.data.confrontation != null
            ? response.data.confrontation
            : null;
        if (!cancelled) {
          setApiDoc(doc);
          if (!doc) setLoadError(true);
        }
      } catch (error) {
        console.error("Error fetching confrontation:", error);
        if (!cancelled) {
          setApiDoc(null);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token, id]);

  const listRow = useMemo(
    () => (apiDoc ? mapApiDocumentToListRow(apiDoc) : null),
    [apiDoc],
  );

  const comparisonRows = useMemo(
    () => mapParentCategoryRowsToComparisonRows(apiDoc?.parentCategoryRows),
    [apiDoc],
  );

  const kpis = useMemo(
    () => computeConfrontationKpis(comparisonRows, apiDoc?.wmae),
    [comparisonRows, apiDoc],
  );

  const sectionTitle = useMemo(
    () => buildDetailSectionTitle(t, listRow, apiDoc?.characterizationDate),
    [t, listRow, apiDoc],
  );

  const handleDelete = async () => {
    try {
      await deleteConfrontation(id);
      setShowDeleteModal(false);
      toast.success(t("confrontation.deletedSuccess"), {
        className: "custom-toast",
      });
      history.push("/confrontation");
    } catch (error) {
      showErrorToast(error, t("confrontation.deleteFailed"), t);
    }
  };

  const actionsMenuItems = isAdmin
    ? [
        {
          label: t("Delete confrontation"),
          icon: <i className="far fa-trash-alt text-[#222222]" aria-hidden />,
          onClick: () => setShowDeleteModal(true),
        },
      ]
    : null;

  if (!token) {
    return <Redirect to={{ pathname: "/login" }} />;
  }

  if (!isLoading && (loadError || !apiDoc)) {
    return <Redirect to={{ pathname: "/confrontation" }} />;
  }

  if (isLoading) {
    return (
      <div className="settings-page-wrapper">
        <PageHeader
          showBackButton
          backRoute="/confrontation"
          backLabel={t("Back to confrontations")}
          title={
            confrontationFromState?.confrontationLabel || t("Confrontations")
          }
          subtitle=""
        />
        <div className="settings-content-wrapper">
          <Card className="table">
            <div className="flex justify-center min-h-[240px] items-center">
              <InlineLoader height="200px" />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const maxCategory = formatConfrontationParentCategoryLabel(
    kpis.maxAbsErrorCategory,
    t,
  );
  const topCategory = formatConfrontationParentCategoryLabel(
    kpis.topContributorCategory,
    t,
  );

  return (
    <div className="settings-page-wrapper">
      <PageHeader
        showBackButton
        backRoute="/confrontation"
        backLabel={t("Back to confrontations")}
        title={sectionTitle}
        titleMultiline
        actionsMenuItems={actionsMenuItems}
        helpButton={<HowItWorksButton topic="confrontations" />}
      />
      <div className="settings-content-wrapper">
        <div className="dashboard-kpi-panel">
          <div className="dashboard-volume-row">
            <HighlightCard
              iconSrc={wmaeIcon}
              title={t("confrontation.wmaeColumn")}
              hint={t("confrontation.wmaeColumnHint")}
              value={formatPercentValue(kpis.wmae, locale) || "—"}
            />
            <HighlightCard
              iconSrc={maxGapIcon}
              title={t("confrontation.maxGap")}
              hint={
                kpis.maxAbsErrorCategory != null
                  ? t("confrontation.maxGapOn", { category: maxCategory })
                  : null
              }
              value={formatPercentValue(kpis.maxAbsError, locale) || "—"}
            />
            <HighlightCard
              iconSrc={contributorIcon}
              title={t("confrontation.topContributor")}
              hint={
                kpis.topContributorShare != null
                  ? t("confrontation.topContributorShare", {
                      value: formatLocaleNumber(
                        kpis.topContributorShare,
                        locale,
                        0,
                      ),
                    })
                  : null
              }
              value={kpis.topContributorCategory != null ? topCategory : "—"}
            />
          </div>
          <div>
            <div className="char-section-header">
              <h2 className="char-section-header-title">
                {t("Comparison by parent category")}
              </h2>
            </div>
            <article className="dashboard-chart-card">
              <ConfrontationComparisonChart rows={comparisonRows} />
            </article>
          </div>
        </div>
      </div>
      {showDeleteModal ? (
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          title={t("Deletion")}
          message={t("confrontation.deleteConfirm")}
          confirmLabel={t("Delete")}
          cancelLabel={t("Cancel")}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      ) : null}
    </div>
  );
};

export default ConfrontationDetail;
